import { useEffect, useState } from "react";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { AskText, SectionHeading } from "./ChecklistRow";
import ConclusionDialog from "./ConclusionDialog";
import ConfirmDialog from "../common/ConfirmDialog";
import { conclusionAsk } from "./sectionRows";
import { EVIDENCE_IN_PROGRESS_STATUSES, EvidenceDraftSummary } from "../../types/evidence";

export default function ConclusionRow({ draft, disabled, rewriting, onSave, onRewrite, embedded }: {
  draft: EvidenceDraftSummary;
  disabled: boolean;
  rewriting: boolean;
  onSave: (paragraphs: [string, string]) => Promise<void>;
  onRewrite: () => void;
  /** Inside the Sections card, which carries the heading: only the status line and its actions. */
  embedded?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [confirmRewrite, setConfirmRewrite] = useState(false);

  // The route can swap the draft under an open dialog; the typed text belongs to the old draft.
  useEffect(() => { setEditing(false); setConfirmRewrite(false); }, [draft.draft_id]);

  const conclusion = draft.conclusion;
  // Both actions read the stored facts of a finished generation, so neither exists before one.
  const neverGenerated = !draft.generated_at;
  const writtenAt = conclusion ? dayjs(conclusion.writtenAt).format("MMM D, HH:mm") : "";

  // A generation in flight is already rewriting it, so the last run's time would mislead.
  const secondary = EVIDENCE_IN_PROGRESS_STATUSES.includes(draft.status)
    ? "Writing…"
    : neverGenerated
      ? "Generate the evidence first — the conclusion is written from it."
      : !conclusion
        ? draft.attention_items?.find((item) => item.code === "conclusion_missing")?.message
          ?? "Conclusion couldn't be written. Retry or write it."
        : conclusion.source === "agent"
          ? `Edited by you at ${writtenAt}`
          : `Written from the case data at ${writtenAt}`;

  // Coloured only when the agent has something to do: a failed conclusion.
  const ask = conclusionAsk(draft);

  const rewrite = () => {
    // Replacing the agent's own words is destructive; replacing the model's is not.
    if (conclusion?.source === "agent") setConfirmRewrite(true);
    else onRewrite();
  };

  const status = (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: embedded ? 0.25 : 1 }}>
      <Typography variant={embedded ? "caption" : "body2"} color="text.secondary" sx={{ flexGrow: 1, minWidth: 0, pt: "2px" }}>
        <AskText severity={ask}>{secondary}</AskText>
      </Typography>
      {!neverGenerated && (
        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
          <Button size="small" variant="text" disabled={disabled} onClick={() => setEditing(true)}>
            {conclusion ? "Edit" : "Write"}
          </Button>
          <Button
            size="small"
            variant={!conclusion ? "outlined" : "text"}
            disabled={disabled || rewriting}
            onClick={rewrite}
          >
            {conclusion ? "Rewrite" : "Retry"}
          </Button>
        </Stack>
      )}
    </Box>
  );

  return (
    <>
      {embedded ? status : (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <SectionHeading>Conclusion</SectionHeading>
          {status}
        </Paper>
      )}

      <ConclusionDialog
        key={draft.draft_id}
        open={editing}
        draftId={draft.draft_id}
        paragraphs={conclusion?.paragraphs ?? null}
        onClose={() => setEditing(false)}
        onSave={onSave}
      />

      <ConfirmDialog
        open={confirmRewrite}
        title="Replace your conclusion?"
        body="This conclusion was written by you. Rewriting replaces it with one written from the case data."
        confirmLabel="Rewrite anyway"
        onClose={() => setConfirmRewrite(false)}
        onConfirm={onRewrite}
      />
    </>
  );
}
